import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

export const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white py-6 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-start justify-between mb-4">
            <Button 
              onClick={() => navigate('/')} 
              variant="ghost" 
              className="text-white hover:text-[#d97706]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <a href="tel:4258902558" className="text-xl font-bold text-white hover:text-[#d97706] transition-colors" data-testid="terms-header-phone-link">
              425.890.2558
            </a>
          </div>
          <h1 className="text-4xl font-bold">Terms of Service</h1>
          <p className="text-gray-300 mt-2">Last Updated: January 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By accessing or using the services of Diamond Trucking School, Inc. ("School," "we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you may not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Enrollment and Registration</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Eligibility Requirements</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              To enroll in our CDL training programs, you must:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Be at least 18 years old (21 for interstate driving)</li>
              <li>Possess a valid driver's license</li>
              <li>Pass a DOT physical examination</li>
              <li>Meet all state and federal requirements for CDL training</li>
              <li>Provide accurate and complete information during registration</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Enrollment Process</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Enrollment is subject to availability and approval by the School. We reserve the right to refuse enrollment to any applicant at our discretion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Tuition and Fees</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Payment Terms</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Tuition and fees must be paid according to the agreed-upon payment schedule. Current pricing includes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Class A CDL: $6,083.00</li>
              <li>Class B CDL: $3,318.00</li>
              <li>Class B to Class A-Upgrade: $3,871.00</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Payment Plans</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Flexible payment plans may be available. All payment arrangements must be made prior to the start of training.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Refund Policy</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refunds are subject to our refund policy, which will be provided in your enrollment agreement. Generally:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Cancellation before training starts may result in a full or partial refund minus administrative fees</li>
              <li>Refunds after training begins are prorated based on hours completed</li>
              <li>No refunds are provided after 60% of the program is completed</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Student Responsibilities</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              As a student, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Attend all scheduled classes and training sessions</li>
              <li>Arrive on time and prepared for training</li>
              <li>Follow all safety protocols and instructor directions</li>
              <li>Maintain appropriate conduct and professionalism</li>
              <li>Complete all required coursework and practice hours</li>
              <li>Respect School property and equipment</li>
              <li>Comply with all federal, state, and local regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">School Policies</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Attendance</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Regular attendance is mandatory. Excessive absences may result in dismissal from the program without refund.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Code of Conduct</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Students must maintain professional behavior at all times. Drug and alcohol use is strictly prohibited. Violation of conduct policies may result in immediate dismissal.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Safety Requirements</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Safety is our top priority. Students must follow all safety procedures and wear required protective equipment. Failure to comply may result in removal from training.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Certification and Licensing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Upon successful completion of the training program, students will receive a certificate of completion. However, obtaining a CDL is subject to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Passing all required written and skills tests administered by the state</li>
              <li>Meeting all state and federal requirements</li>
              <li>Payment of all state licensing fees</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>The School does not guarantee CDL issuance</strong>, as this is determined by state authorities based on individual qualifications and test performance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Job Placement Assistance</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We offer job placement assistance to graduates; however, <strong>we do not guarantee employment</strong>. Job placement is dependent on individual qualifications, market conditions, and employer requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The School and its instructors are not liable for:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Personal injury or property damage during training (except in cases of gross negligence)</li>
              <li>Failure to obtain CDL certification</li>
              <li>Inability to secure employment after graduation</li>
              <li>Changes in state or federal regulations affecting CDL requirements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              All training materials, curriculum, and content provided by the School are proprietary and protected by copyright. Students may not reproduce, distribute, or share these materials without written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Termination</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The School reserves the right to terminate a student's enrollment for:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Violation of School policies or code of conduct</li>
              <li>Non-payment of tuition or fees</li>
              <li>Failure to meet academic or attendance requirements</li>
              <li>Unsafe behavior or failure to follow safety protocols</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Continued use of our services constitutes acceptance of modified terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Governing Law</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service are governed by the laws of the State of Washington. Any disputes shall be resolved in the courts of Snohomish County, Washington.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions about these Terms of Service, please contact:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>Diamond Trucking School, Inc.</strong></p>
              <p className="text-gray-700 mb-2">8217 44th Ave W. Bldg. F, Suite B</p>
              <p className="text-gray-700 mb-2">Mukilteo, WA 98275</p>
              <p className="text-gray-700 mb-2">Phone: (425) 890-2558</p>
              <p className="text-gray-700">Email: diamondtruckingschool@gmail.com</p>
            </div>
          </section>

          <div className="bg-[#d97706] bg-opacity-10 border-l-4 border-[#d97706] p-6 rounded mt-8">
            <p className="text-gray-800 font-semibold">
              By enrolling in Diamond Trucking School, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
